'use client';

import { useBuilding } from '@/app/building/context/BuildingProvider';
import { Reviews } from '@/components/shared/Reviews';
import { Button } from '@/components/ui/Button';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { useAnalytics } from '@/hooks/useAnalytics';
import dynamic from 'next/dynamic';

// Lazy load the modal component
const AllReviewsModal = dynamic(() =>
  import('./modals/AllReviewsModal').then(mod => mod.AllReviewsModal),
);

export function BuildingReviews() {
  const { buildingReviews, isMasked, building } = useBuilding();
  const { count: reviewCount } = buildingReviews;
  const analytics = useAnalytics();

  /*
   * Address-specific H2, replacing the generic "Reviews".
   *
   * Passed as a prop rather than changing `Reviews`' own default: that shared
   * component also backs the owner/contact pages via OwnerReviews, where a
   * building address would be wrong.
   *
   * `Reviews` renders `sectionTitle` in both its branches — the normal list and
   * the "No reviews yet" empty state — so this covers buildings with zero
   * reviews as well.
   *
   * Fallback mirrors the sibling "About {Address}" heading in BuildingAbout,
   * so the two read consistently when an address is somehow missing.
   */
  const reviewsTitle = `Reviews of ${building?.primaryAddress?.streetAddress || 'this building'}`;

  return (
    <Reviews
      reviews={buildingReviews}
      isMasked={isMasked}
      onViewAll={
        <ModalTrigger label="Reviews" content={<AllReviewsModal />} urlKey="reviews">
          <Button
            className="md:self-start w-full md:w-auto py-4 px-6"
            variant="secondary"
            size="sm"
            disabled={isMasked}
            onClick={() => {
              analytics.track('building_view_more_reviews');
            }}
          >
            View all {reviewCount} reviews
          </Button>
        </ModalTrigger>
      }
      sectionTitle={reviewsTitle}
      eventScreen="building"
    />
  );
}
